/** Contenuti e regole di calcolo dell'abbonamento. */

export const PLAN = {
  /** Canone del primo anno, scontato, IVA esclusa */
  annualPrice: 349,
  /** Canone pieno dal rinnovo in poi, IVA esclusa */
  renewalPrice: 499,
  /** Equivalente mensile arrotondato, usato solo come rassicurazione */
  monthlyEquivalent: 29,
  /** Quesiti inclusi per l'intero anno di abbonamento: nessun tetto mensile */
  includedQuestions: 300,
  trialDays: 7,
  /** Prezzo di partenza di un quesito extra: scende al crescere del pacchetto */
  extraQuestionFrom: '1 €',
  /** Per quanto restano spendibili i quesiti extra acquistati */
  extraQuestionsMonths: 12,
} as const;

/**
 * La riga in piccolo sotto al prezzo. Il canone esposto è quello scontato del
 * primo anno: dirlo dove si legge la cifra è l'unico modo perché il rinnovo
 * non arrivi come una sorpresa.
 */
export const PLAN_FIRST_YEAR_NOTE = `Prezzo riservato al primo anno; dal rinnovo ${PLAN.renewalPrice} € + IVA all’anno.`;

/** Le quattro righe dentro la card del piano: il perimetro, non le funzioni. */
export const PLAN_HIGHLIGHTS = [
  `${PLAN.includedQuestions} quesiti inclusi all’anno`,
  'Quesiti extra disponibili quando servono',
  'Utenti e dispositivi illimitati',
  'Nessun costo di attivazione o penale di uscita',
];

/**
 * Che cosa comprende il piano. Tra `**` la parola che dice che cos'è ogni
 * voce: la pagina la mette in grassetto, così l'elenco si scorre in colonna.
 * Una sola per riga (due solo dove la voce è una coppia), mai sparse.
 */
export const PLAN_INCLUDES = [
  '**Quesiti** su fonti ufficiali sincronizzate ogni giorno',
  '**Analisi** di PDF, Word, Excel, TXT, XML, XBRL e immagini',
  '**Redazione** di atti, contratti e documenti professionali',
  '**Pareri e memorandum** strutturati',
  '**Revisione** dei documenti e **confronto** tra versioni',
  '**Fogli di calcolo** con formule modificabili',
  'Trovanorma direttamente **in Word**',
  '**Condivisione** delle conversazioni e lavoro in team',
  '**Storico** delle ricerche e dei documenti prodotti',
  '**Assistenza dedicata** e introduzione guidata',
  'Tutti gli **strumenti professionali**: PDF, conversioni, calcoli, scadenze e altro',
];

/** Come l'abbonamento accompagna i mesi intensi: incluso, extra, riserva. */
export const PLAN_CAPACITY: { title: string; text: string }[] = [
  {
    title: `${PLAN.includedQuestions} quesiti inclusi all’anno`,
    text: `L’abbonamento comprende ${PLAN.includedQuestions} quesiti per tutto l’anno, disponibili per tutto il team: li usi quando ti servono, anche molti nello stesso periodo.`,
  },
  {
    title: 'Aggiungi quesiti quando ti servono',
    text: 'Aggiungi pacchetti di quesiti extra quando ne hai bisogno, per affrontare periodi più intensi o sostenere un utilizzo abitualmente più elevato.',
  },
  {
    title: 'La tua riserva resta disponibile',
    text: `I quesiti si consumano in ordine di scadenza: di regola prima quelli inclusi nell’abbonamento, poi gli extra, che restano disponibili per ${PLAN.extraQuestionsMonths} mesi dall’acquisto.`,
  },
];

/** Una domanda sull'abbonamento. */
export interface PlanFaqItem {
  question: string;
  answer: string;
  /** Una riga in più sotto la risposta, dove serve un elenco */
  detail?: string;
  /** Pagina che completa la risposta: compare come link con freccia in coda */
  link?: { label: string; href: string };
}

/**
 * Le domande sull'abbonamento che stanno sia su /prezzi sia su /faq. Si
 * scrivono solo qui: le due pagine le prendono da questo oggetto, così non
 * possono dire due cose diverse.
 */
export const PLAN_FAQ = {
  questionCount: {
    question: 'Che cosa conta come un quesito?',
    // Chi paga un quesito e chi no, come lo decide il backend: le domande in
    // conversazione e gli strumenti con `tool_costs` in config/file_jobs.php
    // (OCR, riassunto, traduzione…); tutto il resto è gratis. La ricerca nelle
    // fonti non scala quesiti ma ha un tetto antiabuso (500 ogni 30 giorni per
    // utente, config/search.php): per questo il «senza limiti» sta sugli
    // strumenti e non su di lei. Non tutti gli strumenti sono gratis: negli
    // esempi vanno solo quelli senza costo in quesiti.
    // Una riga = un paragrafo, «- » = voce di elenco (lib/answerText).
    answer:
      'In genere, ogni richiesta che porta Trovanorma a svolgere **una ricerca, un’analisi o una rielaborazione** in conversazione **conta come un quesito**, compresi i follow-up. Le richieste incomplete o ambigue, per cui Trovanorma chiede solo un chiarimento, **non vengono conteggiate**.\n' +
      '**Molte funzionalità** comprese nell’abbonamento, invece, **non consumano alcun quesito**:\n' +
      '- la **ricerca nelle fonti**, con la consultazione dei documenti e delle loro **sintesi**;\n' +
      '- i **calcolatori e gli strumenti sui file**, come conversioni, compressione e protezione dei PDF, che puoi usare **senza limiti**.\n' +
      'Il **contatore dei quesiti è sempre visibile**: sai in ogni momento quanti ne hai usati e quanti ne restano, così hai sempre sotto controllo l’utilizzo.',
  },
  extraQuestions: {
    question: 'Come funzionano i quesiti extra?',
    answer: `Restano a disposizione per **${PLAN.extraQuestionsMonths} mesi dall’acquisto**.
I quesiti si consumano **in ordine di scadenza**: di regola prima i ${PLAN.includedQuestions} inclusi nell’abbonamento, poi gli extra, così **la tua riserva resta intatta** finché non serve.
Solo se un pacchetto scade prima del rinnovo dell’abbonamento, quei quesiti vengono usati per primi.`,
  },
  perUser: {
    question: 'Il prezzo è per utente o per studio?',
    answer:
      'L’abbonamento comprende **utenti e dispositivi illimitati**.\n' +
      'Puoi invitare colleghi e collaboratori **senza acquistare una licenza separata** per ogni persona.',
  },
  afterTrial: {
    question: `Cosa succede al termine dei ${PLAN.trialDays} giorni di prova?`,
    answer:
      '**Decidi tu** se attivare l’abbonamento.\n' +
      'Per iniziare la prova **non è richiesta la carta di credito**, quindi non parte alcun addebito automatico.',
  },
  cancel: {
    question: 'Posso interrompere l’abbonamento?',
    answer:
      'Sì. Puoi interromperlo **senza penali**.\n' +
      'Continuerai ad avere accesso a Trovanorma **fino alla fine del periodo di fatturazione già pagato**.',
  },
} satisfies Record<string, PlanFaqItem>;

/** Le domande di /prezzi: quelle in comune con /faq più quelle solo di qui. */
export const PRICING_FAQ: PlanFaqItem[] = [
  PLAN_FAQ.questionCount,
  {
    question: `E se ${PLAN.includedQuestions} quesiti all’anno non mi bastano?`,
    answer:
      'Puoi **aggiungere in qualsiasi momento pacchetti di quesiti extra**, sia per gestire periodi di lavoro più intensi sia se utilizzi Trovanorma abitualmente con volumi superiori a quelli inclusi nell’abbonamento.',
  },
  PLAN_FAQ.extraQuestions,
  {
    question: 'Quanto costano i quesiti extra?',
    answer: `Il costo varia in base alla quantità acquistata: **più quesiti aggiungi, più conveniente diventa il costo unitario**.
I pacchetti partono da **${PLAN.extraQuestionFrom} per quesito extra**.`,
  },
  PLAN_FAQ.perUser,
  {
    question: 'Tutte le funzionalità e gli strumenti sono inclusi?',
    answer:
      'Sì. L’abbonamento dà accesso a **tutte le funzionalità di Trovanorma**: ricerca, analisi documentale, redazione e revisione, pareri e memorandum, fogli di calcolo, Word e collaborazione.\n' +
      'Sono inclusi anche **tutti gli strumenti professionali** disponibili in piattaforma.',
    detail:
      'PDF, conversioni, verifiche, calcoli di interessi e rivalutazioni, scadenze, processo telematico, compensi, risarcimenti e altro.',
    link: { label: 'Scopri tutti gli strumenti', href: '/strumenti/' },
  },
  PLAN_FAQ.afterTrial,
  PLAN_FAQ.cancel,
];
